/**
 * Mirrors backend CommercialPlanCatalog (SubscriptionPlanFeatureSeed.cs).
 * Keep in sync when plan tiers change.
 */

export const COMMERCIAL_TIERS = [
  { key: 'starter', label: 'Starter', seatLimit: 5, highlight: false },
  { key: 'growth', label: 'Growth', seatLimit: 15, highlight: true },
  { key: 'professional', label: 'Professional', seatLimit: 50, highlight: false },
  { key: 'enterprise', label: 'Enterprise', seatLimit: null, highlight: false },
];

export const COMMERCIAL_INDUSTRIES = [
  { key: 'realestate', label: 'Real Estate', codePrefix: 'RE' },
  { key: 'immigration', label: 'Immigration', codePrefix: 'IMM' },
  { key: 'education', label: 'Education', codePrefix: 'EDU' },
];

const FINANCE_HR = new Set([
  'hrDepartments', 'hrLeaveManagement', 'hrHolidays',
  'payroll', 'payslips',
  'financeInvoices', 'financePayments', 'financeExpenses',
  'financeCommissions', 'financeLedger', 'financeTax', 'financeReports',
  'financeHrPack',
  'kpi_payrollSummary', 'kpi_expensePending', 'kpi_revenueMtd',
  'kpi_commissionLiability', 'kpi_feeOutstanding',
]);

const GROWTH_EXTRAS = new Set([
  'teamAttendance', 'notificationEngine', 'marketingAnalytics',
  'kpi_newLeadsToday', 'kpi_callsMade', 'kpi_campaignPerformance',
  'kpi_marketingOverview', 'kpi_marketingFunnel', 'kpi_marketingRoi',
]);

const STARTER_ALLOWED = new Set([
  'dashboard', 'leads', 'assignedLeads', 'pendingFollowUps', 'kanban', 'pipelineStages',
  'settings', 'employees', 'branches', 'quickSupport',
  'kpi_totalLeads', 'kpi_overdueFollowUps', 'kpi_hotLeads', 'kpi_callbacksPending',
  'kpi_assignedLeads', 'kpi_interested', 'kpi_totalCalls', 'kpi_phoneNotPicked',
  'kpi_invalidNumber', 'kpi_callbackLater', 'kpi_complete', 'kpi_conversionFunnel',
  'kpi_todayFollowUps', 'kpi_hotLeadsPanel', 'kpi_activityFeed', 'kpi_teamLeaderboard',
  'kpi_pipelineOverview', 'kpi_callRecords', 'kpi_leadCallStatistics',
]);

const RE_FULL = new Set([
  'buyers', 'propertyListing', 'builders',
  'kpi_upcomingVisits', 'kpi_visited', 'kpi_deals', 'kpi_dealsPerformance',
]);

const IMM_FULL = new Set([
  'applicants', 'cases', 'countries', 'visaTypes', 'documentTemplates', 'counselling',
  'appointments', 'casePayments', 'assignmentRules',
  'kpi_activeCases', 'kpi_documentsPending', 'kpi_applicationsSubmitted', 'kpi_visaApprovals',
  'kpi_caseRevenue', 'kpi_upcomingAppointments', 'kpi_casePipelineOverview',
  'kpi_branchPerformance', 'kpi_countryPerformance',
]);

const EDU_FULL = new Set([
  'institutions', 'courses', 'students', 'admissions', 'educationCounselling',
  'scholarships', 'exams', 'studentAttendance', 'placements',
]);

/** @returns {'yes'|'no'|'addon'} */
export function getPlanFeatureStatus(tier, industryKey, featureCode) {
  if (featureCode === 'socialMediaAutomation') return tier === 'enterprise' ? 'yes' : 'no';

  const isStarter = tier === 'starter';
  const isPro = tier === 'professional' || tier === 'enterprise';
  const isGrowthPlus = isPro || tier === 'growth';

  if (FINANCE_HR.has(featureCode)) return isPro ? 'yes' : 'no';
  if (GROWTH_EXTRAS.has(featureCode)) return isGrowthPlus ? 'yes' : 'no';

  if (RE_FULL.has(featureCode)) {
    if (industryKey !== 'realestate') return 'no';
    return isGrowthPlus ? 'yes' : 'no';
  }
  if (IMM_FULL.has(featureCode)) {
    if (industryKey !== 'immigration') return 'no';
    return isGrowthPlus ? 'yes' : 'no';
  }
  if (EDU_FULL.has(featureCode)) {
    if (industryKey !== 'education') return 'no';
    return isGrowthPlus ? 'yes' : 'no';
  }

  if (isStarter) return STARTER_ALLOWED.has(featureCode) ? 'yes' : 'no';
  return 'yes';
}

/** Comparison table sections — labels match docs/subscription-plans-feature-matrix.md */
export const COMPARISON_SECTIONS = [
  {
    id: 'core',
    title: 'Core CRM features (all industries)',
    industries: null,
    rows: [
      { label: 'Dashboard', key: 'dashboard' },
      { label: 'Leads (list, add, import UI)', key: 'leads' },
      { label: 'Assigned leads', key: 'assignedLeads' },
      { label: 'Pending follow-ups', key: 'pendingFollowUps' },
      { label: 'Kanban pipeline', key: 'kanban' },
      { label: 'Pipeline stages (settings)', key: 'pipelineStages' },
      { label: 'Reports & analytics', key: 'reports' },
      { label: 'My attendance', key: 'attendance' },
      { label: 'Team attendance', key: 'teamAttendance' },
      { label: 'Employees / users', key: 'employees' },
      { label: 'Branches', key: 'branches' },
      { label: 'Workspace settings', key: 'settings' },
      { label: 'Quick support', key: 'quickSupport' },
      { label: 'Notification engine', key: 'notificationEngine' },
      { label: 'Marketing analytics (Meta ROI)', key: 'marketingAnalytics' },
    ],
  },
  {
    id: 'realestate',
    title: 'Real Estate modules',
    industries: ['realestate'],
    rows: [
      { label: 'Buyers', key: 'buyers' },
      { label: 'Property listing', key: 'propertyListing' },
      { label: 'Builders / channel partners', key: 'builders' },
      { label: 'KPI: Upcoming visits', key: 'kpi_upcomingVisits' },
      { label: 'KPI: Visited', key: 'kpi_visited' },
      { label: 'KPI: Deals', key: 'kpi_deals' },
      { label: 'KPI: Deals performance', key: 'kpi_dealsPerformance' },
    ],
  },
  {
    id: 'immigration',
    title: 'Immigration modules',
    industries: ['immigration'],
    rows: [
      { label: 'Applicants', key: 'applicants' },
      { label: 'Cases', key: 'cases' },
      { label: 'Countries', key: 'countries' },
      { label: 'Visa types', key: 'visaTypes' },
      { label: 'Document templates', key: 'documentTemplates' },
      { label: 'Counselling', key: 'counselling' },
      { label: 'Appointments', key: 'appointments' },
      { label: 'Case payments', key: 'casePayments' },
      { label: 'Assignment rules', key: 'assignmentRules' },
    ],
  },
  {
    id: 'education',
    title: 'Education modules',
    industries: ['education'],
    rows: [
      { label: 'Students', key: 'students' },
      { label: 'Admissions', key: 'admissions' },
      { label: 'Institutions', key: 'institutions' },
      { label: 'Courses', key: 'courses' },
      { label: 'Education counselling', key: 'educationCounselling' },
      { label: 'Scholarships', key: 'scholarships' },
      { label: 'Exams', key: 'exams' },
      { label: 'Student attendance', key: 'studentAttendance' },
      { label: 'Placements', key: 'placements' },
    ],
  },
  {
    id: 'finance',
    title: 'Finance & HR pack',
    industries: null,
    rows: [
      { label: 'HR departments', key: 'hrDepartments' },
      { label: 'HR leave management', key: 'hrLeaveManagement' },
      { label: 'HR holidays', key: 'hrHolidays' },
      { label: 'Payroll', key: 'payroll' },
      { label: 'Payslips', key: 'payslips' },
      { label: 'Finance invoices', key: 'financeInvoices' },
      { label: 'Finance payments', key: 'financePayments' },
      { label: 'Finance expenses', key: 'financeExpenses' },
      { label: 'Finance commissions', key: 'financeCommissions' },
      { label: 'Finance ledger', key: 'financeLedger' },
      { label: 'Finance tax', key: 'financeTax' },
      { label: 'Finance reports', key: 'financeReports' },
    ],
  },
  {
    id: 'enterpriseExclusive',
    title: 'Enterprise exclusive',
    industries: null,
    rows: [
      { label: 'Social media automation', key: 'socialMediaAutomation' },
    ],
  },
];

export function planCatalogCode(industryKey, tierKey) {
  const ind = COMMERCIAL_INDUSTRIES.find((i) => i.key === industryKey);
  const tier = tierKey.toUpperCase();
  return ind ? `${ind.codePrefix}_${tier}` : null;
}

export function matchIndustryKey(category) {
  const name = (category?.categoryName ?? category?.CategoryName ?? category?.name ?? '').toLowerCase();
  if (name.includes('immigr')) return 'immigration';
  if (name.includes('educat')) return 'education';
  return 'realestate';
}

export function findPlanForTier(plans, industryKey, tierKey) {
  const code = planCatalogCode(industryKey, tierKey);
  if (!code) return null;
  return (plans ?? []).find((p) => {
    const c = (p?.code ?? p?.Code ?? '').toUpperCase();
    return c === code;
  });
}

export const TIER_PITCH = {
  starter: 'Perfect for small teams — lead capture, assignment, and follow-ups only.',
  growth: 'Full CRM plus industry modules, reports, attendance, and marketing tools.',
  professional: 'Everything in Growth plus Finance & HR for established agencies.',
  enterprise: 'Everything in Professional plus social media automation and unlimited user seats.',
};
